import React, { useState, useEffect, useRef } from 'react';
import './AccessLogs/AccessLogs.css';
import LoginHistoryTab from './AccessLogs/LoginHistoryTab';
import FailedAttemptsTab from './AccessLogs/FailedAttemptsTab';
import PermissionChangesTab from './AccessLogs/PermissionChangesTab';
import UserActivityTimelineTab from './AccessLogs/UserActivityTimelineTab';
import ExportReportsTab from './AccessLogs/ExportReportsTab';
import accessLogsService from '../../services/accessLogsService';

const AccessLogs = ({ canAccessExportReports = true }) => {
  const [activeTab, setActiveTab] = useState('login-history');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('accessLogsDarkMode');
    return saved === 'true';
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollingIntervalRef = useRef(null);

  // Redirect Teams users away from export-reports if they try to access it directly
  useEffect(() => {
    if (activeTab === 'export-reports' && !canAccessExportReports) {
      setActiveTab('login-history');
    }
  }, [activeTab, canAccessExportReports]);

  useEffect(() => {
    // Save dark mode preference
    localStorage.setItem('accessLogsDarkMode', darkMode.toString());
  }, [darkMode]);

  useEffect(() => {
    // Polling for real-time updates (only when tab is visible)
    const startPolling = () => {
      if (document.visibilityState === 'visible') {
        // Poll every 10 seconds
        pollingIntervalRef.current = setInterval(() => {
          setLastUpdated(new Date());
          // Trigger refresh in active tab component
          window.dispatchEvent(new CustomEvent('accessLogsRefresh'));
        }, 10000);
      }
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const tabs = [
    { id: 'login-history', label: 'Login History', icon: '🔐' },
    { id: 'failed-attempts', label: 'Failed Login Attempts', icon: '⚠️' },
    { id: 'permission-changes', label: 'Permission Changes', icon: '🔑' },
    { id: 'user-timeline', label: 'User Activity Timeline', icon: '📊' },
    ...(canAccessExportReports ? [{ id: 'export-reports', label: 'Export Reports', icon: '📥' }] : [])
  ];

  return (
    <div className={`access-logs-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="access-logs-header">
        <h1 className="access-logs-title">Access Logs</h1>
        <div className="access-logs-header-actions">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            className="dark-mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      <div className="access-logs-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`access-logs-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="access-logs-content">
        {activeTab === 'login-history' && <LoginHistoryTab darkMode={darkMode} />}
        {activeTab === 'failed-attempts' && <FailedAttemptsTab darkMode={darkMode} />}
        {activeTab === 'permission-changes' && <PermissionChangesTab darkMode={darkMode} />}
        {activeTab === 'user-timeline' && <UserActivityTimelineTab darkMode={darkMode} />}
        {activeTab === 'export-reports' && !canAccessExportReports && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#dc2626' }}>Feature Not Available</h3>
            <p style={{ marginBottom: 20, color: '#6b7280' }}>
              This feature is only available in Enterprise tier.
            </p>
            <a 
              href="/pricing" 
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#4c51bf',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: 600
              }}
            >
              Upgrade to Enterprise
            </a>
          </div>
        )}
        {activeTab === 'export-reports' && canAccessExportReports && <ExportReportsTab darkMode={darkMode} />}
      </div>
    </div>
  );
};

export default AccessLogs;

